import React, { useState, useEffect, useCallback, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useParams, useSearchParams, Navigate } from "react-router-dom";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "sonner";
import { 
  Play, Pause, Square, FastForward, Clock, Users, Shield, 
  AlertTriangle, Mail, MessageSquare, Globe, Newspaper,
  Settings, ChevronRight, Copy, Check, Activity, Terminal,
  Radio, Zap, Eye, Send, Plus, Trash2, RefreshCw, LogOut, Lock, FileText,
  Edit2, Wand2, ArrowLeft
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Textarea } from "./components/ui/textarea";
import { Badge } from "./components/ui/badge";
import { ScrollArea } from "./components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Checkbox } from "./components/ui/checkbox";
import AnimatorSecondaryDashboard from "./AnimatorDashboard";
import { DemoSelection, DemoAdminRequest, DemoAdminLogin, DemoClientDashboard, DemoAdminDashboard, DemoObserverDashboard, DemoAnimatorDashboard, DemoMultiView, QuoteRequestPage, ObserverDashboard, UserGuidesPage } from "./DemoPages";
import { ScenarioTemplatesList, ScenarioEditor } from "./ScenarioEditor";
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('adminToken');
    const adminData = localStorage.getItem('adminData');
    if (storedToken && adminData) {
      setAdmin(JSON.parse(adminData));
      setToken(storedToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, ...userData } = res.data;
    localStorage.setItem('adminToken', newToken);
    localStorage.setItem('adminData', JSON.stringify(userData));
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setAdmin(userData);
    setToken(newToken);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminData');
    delete axios.defaults.headers.common['Authorization'];
    setAdmin(null);
    setToken(null);
  };

  return (
    <AuthContext.Provider value={{ admin, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

const ProtectedRoute = ({ children }) => {
  const { admin, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen matrix-bg flex items-center justify-center">
        <div className="loading-dot" />
        <div className="loading-dot" />
        <div className="loading-dot" />
      </div>
    );
  }
  
  if (!admin) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Version and Copyright
const APP_VERSION = "V 0.93";
const APP_DATE = "23 janvier 2026";
const APP_EDITOR = "Le Pool Presse SAS";
const EGERIE_IMAGE = "https://customer-assets.emergentagent.com/job_crisis-sim-ai/artifacts/bgij8dxp_image.png";
const ADMIN_EMAIL = "jbgiraud@mac.com";

// Utility Components
const ScanlineOverlay = () => <div className="scanline-overlay" />;

const ScenarioClock = ({ time, status, compression }) => {
  // Format time to show only HH:MM
  const displayTime = time && time.includes('T') ? time.split('T')[1]?.substring(0, 5) : (time || "00:00");
  
  return (
  <div className="flex items-center gap-4">
    <div className="flex flex-col items-center">
      <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Heure Scénario</span>
      <span className="digital-clock">{displayTime}</span>
    </div>
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <span className={`status-dot ${status === 'running' ? 'active' : status === 'paused' ? 'paused' : 'alert'}`} />
        <span className="font-mono text-xs uppercase">{status || 'setup'}</span>
      </div>
      {compression !== 1 && (
        <span className="font-mono text-[10px] text-muted-foreground">x{compression} vitesse</span>
      )}
    </div>
  </div>
);
};

const MessageCard = ({ message, onClick }) => {
  const platformIcons = {
    twitter: <Globe className="w-4 h-4 text-[#1DA1F2]" />,
    facebook: <Globe className="w-4 h-4 text-[#4267B2]" />,
    instagram: <Globe className="w-4 h-4 text-[#E1306C]" />,
    email: <Mail className="w-4 h-4" />,
    sms: <MessageSquare className="w-4 h-4" />,
    flash_info: <Radio className="w-4 h-4 text-destructive" />,
    phone_call: <Activity className="w-4 h-4 text-yellow-500" />,
    media_arrival: <Newspaper className="w-4 h-4 text-primary" />,
    social_post: <Globe className="w-4 h-4 text-primary" />,
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className={`message-card ${!message.is_read ? 'unread' : ''} ${message.message_type === 'flash_info' ? 'alert' : ''} cursor-pointer`}
      onClick={onClick}
      data-testid={`message-${message.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          {platformIcons[message.message_type] || platformIcons[message.platform] || <Terminal className="w-4 h-4" />}
          <span className="font-mono text-xs text-muted-foreground">{message.sender}</span>
        </div>
        <span className="font-mono text-[10px] text-muted-foreground">{message.scenario_time}</span>
      </div>
      {message.subject && (
        <h4 className="text-sm font-semibold mb-1 text-primary">{message.subject}</h4>
      )}
      <p className="text-sm text-foreground/80 line-clamp-3">{message.content}</p>
      <div className="flex items-center gap-2 mt-2">
        <Badge variant="outline" className="badge-matrix text-[10px]">
          {message.message_type}
        </Badge>
        <Badge variant="outline" className="badge-matrix text-[10px]">
          {message.language}
        </Badge>
      </div>
    </motion.div>
  );
};

const PauseOverlay = ({ show }) => (
  <AnimatePresence>
    {show && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="pause-overlay"
      >
        <div className="text-center">
          <motion.div
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="pause-text"
          >
            PAUSE
          </motion.div>
          <p className="font-mono text-muted-foreground mt-4">Simulation en pause - En attente de l'animateur</p>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

// Admin Login Page
// Intensity Meter Component
const IntensityMeter = ({ sessionId, messages }) => {
  const [intensity, setIntensity] = useState(3);
  const [history, setHistory] = useState([]);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    // Calculate intensity based on recent messages
    const recentMessages = messages.slice(-15);
    let calc = 3;
    recentMessages.forEach(msg => {
      if (msg.message_type === 'flash_info') calc += 1.5;
      else if (msg.message_type === 'media_arrival') calc += 2;
      else if (msg.message_type === 'phone_call') calc += 1;
      else calc += 0.3;
    });
    setIntensity(Math.min(10, Math.max(1, calc)));
    
    // Build history
    const hist = messages.map((msg, i) => ({
      time: msg.scenario_time || `+${i * 5}m`,
      intensity: Math.min(10, 3 + (i * 0.4) + (msg.message_type === 'flash_info' ? 2 : 0))
    }));
    setHistory(hist);
  }, [messages]);

  const getIntensityColor = (level) => {
    if (level <= 3) return 'bg-green-500';
    if (level <= 5) return 'bg-yellow-500';
    if (level <= 7) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getIntensityLabel = (level) => {
    if (level <= 3) return 'CALME';
    if (level <= 5) return 'TENSION';
    if (level <= 7) return 'CRISE';
    return 'CRITIQUE';
  };

  return (
    <div className="crisis-panel p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-primary" />
          <span className="font-mono text-xs uppercase">Intensité de crise</span>
        </div>
        <button 
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-muted-foreground hover:text-primary"
        >
          {expanded ? '▲ Réduire' : '▼ Détails'}
        </button>
      </div>
      
      {/* Main meter */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-4 bg-secondary rounded-full overflow-hidden">
          <motion.div
            className={`h-full ${getIntensityColor(intensity)} transition-all`}
            initial={{ width: 0 }}
            animate={{ width: `${intensity * 10}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
        <div className="flex items-center gap-2">
          <span className={`font-mono text-lg font-bold ${intensity > 7 ? 'text-red-500' : intensity > 5 ? 'text-orange-500' : 'text-primary'}`}>
            {intensity.toFixed(1)}
          </span>
          <Badge className={`${getIntensityColor(intensity)} text-white text-xs`}>
            {getIntensityLabel(intensity)}
          </Badge>
        </div>
      </div>

      {/* Expanded history chart */}
      <AnimatePresence>
        {expanded && history.length > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="mt-3 pt-3 border-t border-border"
          >
            <div className="h-20 flex items-end gap-[2px]">
              {history.slice(-30).map((point, idx) => (
                <div
                  key={idx}
                  className={`flex-1 ${getIntensityColor(point.intensity)} rounded-t opacity-70 hover:opacity-100 transition-opacity`}
                  style={{ height: `${point.intensity * 10}%` }}
                  title={`${point.time}: ${point.intensity.toFixed(1)}`}
                />
              ))}
            </div>
            <div className="flex justify-between mt-1">
              <span className="font-mono text-[10px] text-muted-foreground">Début</span>
              <span className="font-mono text-[10px] text-muted-foreground">Maintenant</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Manual Input Panel Component
const ManualInputPanel = ({ sessionId, participants, onSent }) => {
  const [inputType, setInputType] = useState('text');
  const [targetType, setTargetType] = useState('all');
  const [targetId, setTargetId] = useState('');
  const [content, setContent] = useState('');
  const [source, setSource] = useState('Admin');
  const [generating, setGenerating] = useState(false);
  const [radioStation, setRadioStation] = useState('France Info');
  const [scheduleTime, setScheduleTime] = useState('');
  const [isScheduled, setIsScheduled] = useState(false);

  const sendInput = async () => {
    if (!content.trim()) {
      toast.error("Contenu requis");
      return;
    }
    try {
      const payload = {
        session_id: sessionId,
        target_type: targetType,
        target_id: targetType !== 'all' ? targetId : null,
        input_type: inputType,
        content,
        source: inputType === 'radio_flash' ? radioStation : source,
        generate_with_ai: false
      };
      
      // Add scheduling if set
      if (isScheduled && scheduleTime) {
        payload.scheduled_time = scheduleTime;
      }
      
      await axios.post(`${API}/sessions/${sessionId}/manual-input`, payload);
      
      if (inputType === 'radio_flash') {
        toast.success(isScheduled ? `📻 Flash programmé à ${scheduleTime}` : "📻 Flash radio diffusé !", {
          description: radioStation
        });
      } else {
        toast.success("Input envoyé !");
      }
      setContent('');
      setScheduleTime('');
      setIsScheduled(false);
      onSent?.();
    } catch (err) {
      toast.error("Erreur d'envoi");
    }
  };

  const generateWithAI = async () => {
    if (!content.trim()) {
      toast.error("Décrivez ce que vous voulez générer");
      return;
    }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/sessions/${sessionId}/manual-input`, {
        session_id: sessionId,
        target_type: targetType,
        target_id: targetType !== 'all' ? targetId : null,
        input_type: inputType,
        content,
        source: inputType === 'radio_flash' ? radioStation : source,
        generate_with_ai: true
      });
      toast.success(inputType === 'radio_flash' ? "📻 Flash IA diffusé !" : "Contenu IA envoyé !");
      setContent('');
      onSent?.();
    } catch (err) {
      toast.error("Erreur de génération");
    }
    setGenerating(false);
  };

  return (
    <div className="crisis-panel p-4 mt-4">
      <h3 className="heading text-sm text-primary mb-3 flex items-center gap-2">
        <Send className="w-4 h-4" /> Envoi Manuel
      </h3>
      
      <div className="space-y-3">
        {/* Type selection */}
        <div className="flex gap-2 flex-wrap">
          {[
            { key: 'text', label: 'Texte', icon: '📝' },
            { key: 'image', label: 'Image IA', icon: '🖼️' },
            { key: 'phone_call', label: 'Appel', icon: '📞' },
            { key: 'radio_flash', label: 'Flash Radio', icon: '📻' }
          ].map(t => (
            <button
              key={t.key}
              className={`flex-1 py-2 px-3 text-xs border rounded transition-colors ${
                inputType === t.key 
                  ? t.key === 'radio_flash' 
                    ? 'border-red-500 bg-red-500/20 text-red-400' 
                    : 'border-primary bg-primary/20' 
                  : 'border-border hover:border-primary/50'
              }`}
              onClick={() => setInputType(t.key)}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* Radio Flash Options */}
        {inputType === 'radio_flash' && (
          <div className="bg-red-950/30 border border-red-500/30 rounded p-3 space-y-2">
            <div className="flex items-center gap-2">
              <Radio className="w-4 h-4 text-red-400" />
              <span className="text-xs text-red-400 font-mono">Configuration Flash Radio</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <select
                className="input-matrix h-9 text-sm bg-red-950/50 border-red-500/30"
                value={radioStation}
                onChange={e => setRadioStation(e.target.value)}
              >
                <option value="France Info">📻 France Info</option>
                <option value="Europe 1">📻 Europe 1</option>
                <option value="RTL">📻 RTL</option>
                <option value="France Inter">📻 France Inter</option>
                <option value="RMC">📻 RMC</option>
              </select>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="schedule"
                  checked={isScheduled}
                  onChange={e => setIsScheduled(e.target.checked)}
                  className="w-4 h-4"
                />
                <label htmlFor="schedule" className="text-xs text-muted-foreground">Programmer</label>
                {isScheduled && (
                  <Input
                    type="time"
                    className="input-matrix h-8 text-xs w-24"
                    value={scheduleTime}
                    onChange={e => setScheduleTime(e.target.value)}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Target selection (not for radio_flash) */}
        {inputType !== 'radio_flash' && (
          <div className="grid grid-cols-2 gap-2">
            <select
              className="input-matrix h-9 text-sm"
              value={targetType}
              onChange={e => setTargetType(e.target.value)}
            >
              <option value="all">📢 Tous les joueurs</option>
              <option value="specific_player">👤 Joueur spécifique</option>
              <option value="specific_role">👥 Rôle spécifique</option>
            </select>
            
            {targetType !== 'all' && (
              <select
                className="input-matrix h-9 text-sm"
                value={targetId}
                onChange={e => setTargetId(e.target.value)}
              >
                <option value="">Sélectionner...</option>
                {participants?.map(p => (
                  <option key={p.id} value={p.id}>{p.name || p.role_name}</option>
                ))}
              </select>
            )}
          </div>
        )}

        {/* Source (not for radio_flash) */}
        {inputType !== 'radio_flash' && (
          <Input
            className="input-matrix h-9 text-sm"
            placeholder="Source (ex: AFP, France Info, Email interne...)"
            value={source}
            onChange={e => setSource(e.target.value)}
          />
        )}

        {/* Content */}
        <Textarea
          className={`input-matrix text-sm min-h-[80px] ${inputType === 'radio_flash' ? 'border-red-500/30' : ''}`}
          placeholder={inputType === 'image' 
            ? "Décrivez l'image à générer..." 
            : inputType === 'phone_call'
            ? "Script de l'appel téléphonique..."
            : inputType === 'radio_flash'
            ? "Contenu du flash radio (sera lu par la synthèse vocale)..."
            : "Contenu du message..."
          }
          value={content}
          onChange={e => setContent(e.target.value)}
        />

        {/* Actions */}
        <div className="flex gap-2">
          <Button 
            className={`flex-1 ${inputType === 'radio_flash' ? 'bg-red-600 hover:bg-red-700' : 'bg-primary'} text-primary-foreground`}
            onClick={sendInput}
            disabled={!content.trim()}
          >
            {inputType === 'radio_flash' ? (
              <>
                <Radio className="w-4 h-4 mr-2" /> 
                {isScheduled ? 'Programmer' : 'Diffuser'}
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" /> Envoyer
              </>
            )}
          </Button>
          <Button
            variant="outline"
            className="flex-1"
            onClick={generateWithAI}
            disabled={!content.trim() || generating}
          >
            {generating ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Wand2 className="w-4 h-4 mr-2" />
            )}
            Générer IA
          </Button>
        </div>
      </div>
    </div>
  );
};

const AdminLogin = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      toast.error("Email et mot de passe requis");
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Connexion réussie");
      navigate("/admin");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Identifiants incorrects");
    }
    setLoading(false);
  };

  return (
    <div className="landing-hero">
      <ScanlineOverlay />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <div className="mb-8">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mb-4"
          >
            <img 
              src={EGERIE_IMAGE} 
              alt="Crisis Simulator AI" 
              className="w-32 h-32 mx-auto object-cover rounded-full border-2 border-primary glow-green"
              style={{ objectPosition: 'center 20%' }}
            />
          </motion.div>
          <h1 className="logo-text text-2xl" data-testid="login-title">Administration</h1>
          <p className="font-mono text-sm text-muted-foreground mt-2">CRISIS SIMULATOR AI</p>
        </div>

        <form onSubmit={handleLogin} className="crisis-panel p-8 max-w-md mx-auto">
          <div className="space-y-4">
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-2">
                Email
              </label>
              <Input
                type="email"
                className="input-matrix"
                placeholder="admin@simulationdecrise.fr"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
              />
            </div>

            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-2">
                Mot de passe
              </label>
              <Input
                type="password"
                className="input-matrix"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider mt-4"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <div className="loading-dot" />
                  <div className="loading-dot" />
                  <div className="loading-dot" />
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4 mr-2" /> Connexion
                </>
              )}
            </Button>

            <div className="pt-4 border-t border-border mt-4">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => navigate("/")}
              >
                Accès Participant
              </Button>
            </div>
          </div>
        </form>

        <div className="mt-8 space-y-1">
          <p className="font-mono text-[10px] text-muted-foreground">
            simulationdecrise.fr
          </p>
          <p className="font-mono text-[10px] text-muted-foreground">
            © {APP_DATE} {APP_EDITOR} - {APP_VERSION}
          </p>
        </div>
      </motion.div>
    </div>
  );
};

// Admin Dashboard (Session List)
const AdminDashboard = () => {
  const navigate = useNavigate();
  const { admin, logout } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sessionsRes, scenariosRes] = await Promise.all([
          axios.get(`${API}/sessions`),
          axios.get(`${API}/scenarios`)
        ]);
        setSessions(sessionsRes.data.sessions || []);
        setScenarios(scenariosRes.data.scenarios || []);
      } catch (err) {
        toast.error("Erreur lors du chargement");
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const getScenarioName = (id) => {
    const scenario = scenarios.find(s => s.id === id);
    return scenario?.name || "Scénario inconnu";
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen matrix-bg">
      <ScanlineOverlay />
      <div className="crisis-header">
        <div className="flex items-center gap-4">
          <Shield className="w-8 h-8 text-primary" />
          <div>
            <h1 className="heading text-lg text-primary">Administration</h1>
            <p className="font-mono text-xs text-muted-foreground">{admin?.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate("/admin/scenarios")}
            data-testid="scenario-editor-btn"
          >
            <Edit2 className="w-4 h-4 mr-2" /> Éditeur Scénarios
          </Button>
          <Button
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            onClick={() => navigate("/scenarios")}
            data-testid="new-simulation-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Nouvelle Simulation
          </Button>
          <Button variant="outline" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Déconnexion
          </Button>
        </div>
      </div>

      <div className="p-6 max-w-6xl mx-auto">
        <h2 className="heading text-2xl text-primary mb-6">Sessions ({sessions.length})</h2>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="loading-dot" />
            <div className="loading-dot" />
            <div className="loading-dot" />
          </div>
        ) : sessions.length === 0 ? (
          <div className="crisis-panel p-12 text-center">
            <Activity className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="heading text-xl text-muted-foreground">Aucune session</h3>
            <p className="text-muted-foreground mt-2">Créez votre première simulation de crise</p>
            <Button
              className="mt-6 bg-primary text-primary-foreground"
              onClick={() => navigate("/scenarios")}
            >
              <Plus className="w-4 h-4 mr-2" /> Créer une simulation
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sessions.map((session, index) => (
              <motion.div
                key={session.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="crisis-panel p-4 cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => navigate(`/setup/${session.id}`)}
                data-testid={`session-${session.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <Badge className={`badge-matrix ${session.status === 'running' ? '' : session.status === 'completed' ? 'opacity-50' : 'warning'}`}>
                    {session.status}
                  </Badge>
                  <span className="font-mono text-xs text-primary">{session.access_code}</span>
                </div>
                <h3 className="font-semibold text-lg">{session.company_name}</h3>
                <p className="text-sm text-muted-foreground">{getScenarioName(session.scenario_id)}</p>
                <div className="flex items-center gap-4 mt-3">
                  <span className="font-mono text-xs text-muted-foreground">
                    <Users className="w-3 h-3 inline mr-1" />
                    {session.participants?.length || 0} participants
                  </span>
                  <span className="font-mono text-xs text-muted-foreground">
                    <Clock className="w-3 h-3 inline mr-1" />
                    x{session.time_compression}
                  </span>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button
                    size="sm"
                    className="flex-1 bg-primary/20 text-primary hover:bg-primary/30"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/animator/${session.id}`);
                    }}
                  >
                    <Play className="w-3 h-3 mr-1" /> Animer
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/setup/${session.id}`);
                    }}
                  >
                    <Settings className="w-3 h-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-green-400 border-green-500/50 hover:bg-green-950"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/print-fiches/${session.id}`);
                    }}
                    title="Imprimer les fiches de connexion"
                  >
                    <FileText className="w-3 h-3" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Landing Page
const Landing = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [accessCode, setAccessCode] = useState("");
  const [accessToken, setAccessToken] = useState(searchParams.get('token') || "");
  const [loading, setLoading] = useState(false);

  // Auto-join if token in URL
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      handleJoinByToken(token);
    }
  }, []);

  const handleJoinByCode = async () => {
    if (!accessCode.trim()) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sessions/code/${accessCode}`);
      navigate(`/setup/${res.data.id}`);
    } catch (err) {
      toast.error("Code d'accès invalide");
    }
    setLoading(false);
  };

  const handleJoinByToken = async (token = accessToken) => {
    if (token === "GuineaPig") {
      // Magic participant token - show session list for demo
      navigate("/participant-select");
      return;
    }
    
    if (!token.trim()) return;
    setLoading(true);
    try {
      // First try as animator
      try {
        const animRes = await axios.get(`${API}/join/animator/${token}`);
        if (animRes.data.animator) {
          navigate(`/animator-view/${animRes.data.session.id}?token=${token}`);
          setLoading(false);
          return;
        }
      } catch (e) {
        // Not an animator, try as participant
      }
      
      const res = await axios.get(`${API}/join/${token}`);
      const { session, participant } = res.data;
      if (participant.is_main_animator) {
        navigate(`/animator/${session.id}`);
      } else if (participant.is_animator) {
        navigate(`/animator-view/${session.id}?token=${token}`);
      } else {
        navigate(`/player/${session.id}?token=${token}`);
      }
    } catch (err) {
      toast.error("Token d'accès invalide");
    }
    setLoading(false);
  };

  return (
    <div className="landing-hero">
      <ScanlineOverlay />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <div className="mb-8">
          {/* Égérie Image */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mb-6"
          >
            <img 
              src={EGERIE_IMAGE} 
              alt="Crisis Simulator AI - Égérie" 
              className="w-48 h-48 mx-auto object-cover rounded-full border-2 border-primary glow-green"
              style={{ objectPosition: 'center 20%' }}
            />
          </motion.div>
          
          <h1 className="logo-text" data-testid="app-title">Crisis Simulator AI</h1>
          <p className="font-mono text-sm text-muted-foreground mt-2">PLATEFORME DE SIMULATION DE CRISE</p>
        </div>

        <div className="crisis-panel p-8 max-w-md mx-auto">
          <div className="space-y-6">
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-2">
                Code Session
              </label>
              <div className="flex gap-2">
                <Input
                  className="input-matrix flex-1"
                  placeholder="XXXXXXXX"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                  data-testid="access-code-input"
                />
                <Button 
                  className="btn-matrix" 
                  onClick={handleJoinByCode}
                  disabled={loading}
                  data-testid="join-code-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground font-mono">ou</span>
              </div>
            </div>

            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-2">
                Token d'accès (Participant)
              </label>
              <div className="flex gap-2">
                <Input
                  className="input-matrix flex-1"
                  placeholder="Votre token..."
                  value={accessToken}
                  onChange={(e) => setAccessToken(e.target.value)}
                  data-testid="access-token-input"
                />
                <Button 
                  className="btn-matrix" 
                  onClick={() => handleJoinByToken()}
                  disabled={loading}
                  data-testid="join-token-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="pt-4 border-t border-border">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate("/login")}
                data-testid="admin-login-btn"
              >
                <Lock className="w-4 h-4 mr-2" />
                Accès Administrateur
              </Button>
            </div>

            <div className="pt-4">
              <Button
                className="w-full bg-destructive/20 text-destructive hover:bg-destructive/30 border border-destructive/50"
                onClick={() => navigate("/demo")}
                data-testid="demo-btn"
              >
                <Eye className="w-4 h-4 mr-2" />
                Mode Démo
              </Button>
            </div>

            <div className="pt-2">
              <Button
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold"
                onClick={() => navigate("/quote")}
                data-testid="quote-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Demander un Devis
              </Button>
            </div>

            <div className="pt-2">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate("/guides")}
                data-testid="guides-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                📋 Modes d'Emploi
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-8 space-y-1">
          <p className="font-mono text-[10px] text-muted-foreground">
            simulationdecrise.fr
          </p>
          <p className="font-mono text-[10px] text-muted-foreground">
            © {APP_DATE} {APP_EDITOR} - {APP_VERSION}
          </p>
        </div>
      </motion.div>
    </div>
  );
};

// Participant Session Select (for GuineaPig token)
const ParticipantSessionSelect = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const res = await axios.get(`${API}/sessions`);
        setSessions((res.data.sessions || []).filter(s => s.status !== 'completed'));
      } catch (err) {
        toast.error("Erreur lors du chargement");
      }
      setLoading(false);
    };
    fetchSessions();
  }, []);

  return (
    <div className="min-h-screen matrix-bg p-8">
      <ScanlineOverlay />
      <div className="max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-primary heading">Sessions Disponibles</h1>
              <p className="font-mono text-sm text-muted-foreground mt-1">Accès Participant</p>
            </div>
            <Button variant="outline" onClick={() => navigate("/")} data-testid="back-btn">
              Retour
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="loading-dot" />
              <div className="loading-dot" />
              <div className="loading-dot" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="crisis-panel p-12 text-center">
              <Activity className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="heading text-xl text-muted-foreground">Aucune session active</h3>
              <p className="text-muted-foreground mt-2">Contactez votre animateur</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {sessions.map((session, index) => (
                <motion.div
                  key={session.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="crisis-panel p-6 cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => navigate(`/player/${session.id}`)}
                >
                  <Badge className={`badge-matrix mb-2 ${session.status === 'running' ? '' : 'warning'}`}>
                    {session.status}
                  </Badge>
                  <h3 className="font-semibold text-xl text-primary">{session.company_name}</h3>
                  <p className="text-muted-foreground mt-2">{session.location}</p>
                  <div className="flex items-center gap-4 mt-4">
                    <Badge variant="outline" className="badge-matrix">
                      <Users className="w-3 h-3 mr-1" />
                      {session.participants?.length || 0}
                    </Badge>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

// Scenario Selection Page
const ScenarioSelection = () => {
  const navigate = useNavigate();
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchScenarios = async () => {
      try {
        const res = await axios.get(`${API}/scenarios`);
        setScenarios(res.data.scenarios || []);
      } catch (err) {
        toast.error("Erreur lors du chargement des scénarios");
      }
      setLoading(false);
    };
    fetchScenarios();
  }, []);

  const crisisTypeIcons = {
    incendie: <AlertTriangle className="w-6 h-6 text-destructive" />,
    cyberattaque: <Terminal className="w-6 h-6 text-primary" />,
    produit: <Shield className="w-6 h-6 text-yellow-500" />,
    maritime: <Globe className="w-6 h-6 text-blue-400" />,
    sociale: <Users className="w-6 h-6 text-purple-400" />,
  };

  return (
    <div className="min-h-screen matrix-bg p-8">
      <ScanlineOverlay />
      <div className="max-w-6xl mx-auto">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-primary heading">Sélection du Scénario</h1>
              <p className="font-mono text-sm text-muted-foreground mt-1">Choisissez le type de crise à simuler</p>
            </div>
            <Button variant="outline" onClick={() => navigate("/")} data-testid="back-btn">
              Retour
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="loading-dot" />
              <div className="loading-dot" />
              <div className="loading-dot" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {scenarios.map((scenario, index) => (
                <motion.div
                  key={scenario.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="crisis-panel p-6 cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => navigate(`/setup/${scenario.id}/new`)}
                  data-testid={`scenario-${scenario.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-secondary rounded">
                      {crisisTypeIcons[scenario.crisis_type] || <Shield className="w-6 h-6" />}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg heading text-primary">{scenario.name}</h3>
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-3">{scenario.description}</p>
                      <div className="flex items-center gap-4 mt-4">
                        <Badge variant="outline" className="badge-matrix">
                          <Clock className="w-3 h-3 mr-1" />
                          {scenario.duration_minutes} min
                        </Badge>
                        <Badge variant="outline" className="badge-matrix">
                          <Users className="w-3 h-3 mr-1" />
                          {scenario.roles?.length || 0} rôles
                        </Badge>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

// Session Setup Page
const SessionSetup = () => {
  const navigate = useNavigate();
  const { scenarioId, sessionId } = useParams();
  const isNew = sessionId === "new";
  
  const [scenario, setScenario] = useState(null);
  const [session, setSession] = useState(null);
  const [companyName, setCompanyName] = useState("");
  const [location, setLocation] = useState("");
  const [timeCompression, setTimeCompression] = useState("1");
  const [configAnswers, setConfigAnswers] = useState({});
  const [enabledLanguages, setEnabledLanguages] = useState(["fr"]);
  const [participants, setParticipants] = useState([]);
  const [animators, setAnimators] = useState([]);
  const [newParticipant, setNewParticipant] = useState({ name: "", role_id: "", is_animator: false });
  const [newAnimator, setNewAnimator] = useState({ name: "", assigned_roles: [] });
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);
  const [activeTab, setActiveTab] = useState("config");

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (isNew) {
          const res = await axios.get(`${API}/scenarios/${scenarioId}`);
          setScenario(res.data);
        } else {
          const sessionRes = await axios.get(`${API}/sessions/${sessionId || scenarioId}`);
          setSession(sessionRes.data);
          setCompanyName(sessionRes.data.company_name);
          setLocation(sessionRes.data.location || "");
          setTimeCompression(String(sessionRes.data.time_compression));
          setConfigAnswers(sessionRes.data.config_answers || {});
          setEnabledLanguages(sessionRes.data.enabled_languages || ["fr"]);
          setParticipants(sessionRes.data.participants || []);
          setAnimators(sessionRes.data.animators || []);
          
          const scenRes = await axios.get(`${API}/scenarios/${sessionRes.data.scenario_id}`);
          setScenario(scenRes.data);
        }
      } catch (err) {
        toast.error("Erreur lors du chargement");
      }
      setLoading(false);
    };
    fetchData();
  }, [scenarioId, sessionId, isNew]);

  const createSession = async () => {
    if (!companyName.trim()) {
      toast.error("Le nom de l'entreprise est requis");
      return;
    }
    try {
      const res = await axios.post(`${API}/sessions`, {
        scenario_id: scenario.id,
        company_name: companyName,
        location,
        time_compression: parseFloat(timeCompression),
        config_answers: configAnswers,
        enabled_languages: enabledLanguages
      });
      toast.success("Session créée avec succès");
      navigate(`/setup/${res.data.id}`);
    } catch (err) {
      toast.error("Erreur lors de la création de la session");
    }
  };

  const addParticipant = async () => {
    if (!newParticipant.name.trim() || !newParticipant.role_id) {
      toast.error("Nom et rôle requis");
      return;
    }
    try {
      const res = await axios.post(`${API}/sessions/${session.id}/participants`, newParticipant);
      setParticipants([...participants, res.data]);
      setNewParticipant({ name: "", role_id: "", is_animator: false });
      toast.success("Participant ajouté");
    } catch (err) {
      toast.error("Erreur lors de l'ajout du participant");
    }
  };

  const removeParticipant = async (participantId) => {
    try {
      await axios.delete(`${API}/sessions/${session.id}/participants/${participantId}`);
      setParticipants(participants.filter(p => p.id !== participantId));
      toast.success("Participant supprimé");
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const addAnimator = async () => {
    if (!newAnimator.name.trim()) {
      toast.error("Nom de l'animateur requis");
      return;
    }
    try {
      const res = await axios.post(`${API}/sessions/${session.id}/animators`, newAnimator);
      setAnimators([...animators, res.data]);
      setNewAnimator({ name: "", assigned_roles: [] });
      toast.success("Animateur ajouté");
    } catch (err) {
      toast.error("Erreur lors de l'ajout de l'animateur");
    }
  };

  const removeAnimator = async (animatorId) => {
    try {
      await axios.delete(`${API}/sessions/${session.id}/animators/${animatorId}`);
      setAnimators(animators.filter(a => a.id !== animatorId));
      toast.success("Animateur supprimé");
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    toast.success("Copié !");
    setTimeout(() => setCopied(null), 2000);
  };

  const getAccessUrl = (token) => `${window.location.origin}/?token=${token}`;

  // Get available animator roles from events (phone_call, media_arrival, etc.)
  const getAnimatorRoles = () => {
    if (!scenario?.events) return [];
    const roles = new Set();
    scenario.events.forEach(event => {
      if (event.event_type === 'phone_call' || event.event_type === 'media_arrival') {
        roles.add(event.source);
      }
    });
    return Array.from(roles);
  };

  const languages = [
    { code: "fr", name: "Français" },
    { code: "en", name: "English" },
    { code: "de", name: "Deutsch" },
    { code: "es", name: "Español" },
    { code: "it", name: "Italiano" },
  ];

  if (loading) {
    return (
      <div className="min-h-screen matrix-bg flex items-center justify-center">
        <div className="loading-dot" />
        <div className="loading-dot" />
        <div className="loading-dot" />
      </div>
    );
  }

  return (
    <div className="min-h-screen matrix-bg">
      <ScanlineOverlay />
      <div className="crisis-header">
        <div className="flex items-center gap-4">
          <Shield className="w-8 h-8 text-primary" />
          <div>
            <h1 className="heading text-lg text-primary">{scenario?.name}</h1>
            <p className="font-mono text-xs text-muted-foreground">Configuration de la simulation</p>
          </div>
        </div>
        {session && (
          <div className="flex items-center gap-4">
            <div className="text-right">
              <span className="font-mono text-xs text-muted-foreground block">Code Session</span>
              <span className="font-mono text-xl text-primary font-bold">{session.access_code}</span>
            </div>
            <Button
              className="btn-matrix"
              onClick={() => copyToClipboard(session.access_code, 'code')}
            >
              {copied === 'code' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
        )}
      </div>

      <div className="p-6 max-w-6xl mx-auto">
        {/* Tabs for different sections */}
        {session && (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
            <TabsList className="bg-card border border-border p-1">
              <TabsTrigger value="config" className="tab-matrix data-[state=active]:text-primary">
                <Settings className="w-4 h-4 mr-2" /> Configuration
              </TabsTrigger>
              <TabsTrigger value="participants" className="tab-matrix data-[state=active]:text-primary">
                <Users className="w-4 h-4 mr-2" /> Participants ({participants.length})
              </TabsTrigger>
              <TabsTrigger value="animators" className="tab-matrix data-[state=active]:text-primary">
                <Activity className="w-4 h-4 mr-2" /> Animateurs ({animators.length})
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Configuration Panel */}
          {(isNew || activeTab === "config") && (
            <div className="crisis-panel p-6">
              <h2 className="heading text-xl text-primary mb-6">Configuration</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-2">
                    Nom de l'entreprise *
                  </label>
                  <Input
                    className="input-matrix"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Ex: ACME Corporation"
                    disabled={!isNew}
                    data-testid="company-name-input"
                  />
                </div>

                <div>
                  <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-2">
                    Lieu (optionnel)
                  </label>
                  <Input
                    className="input-matrix"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="Ex: Paris, France"
                    disabled={!isNew}
                  />
                </div>

                <div>
                  <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-2">
                    Compression temporelle
                  </label>
                  <Select value={timeCompression} onValueChange={setTimeCompression} disabled={!isNew}>
                    <SelectTrigger className="input-matrix">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Temps réel (1x)</SelectItem>
                      <SelectItem value="2">2x (3h en 1h30)</SelectItem>
                      <SelectItem value="4">4x (3h en 45min)</SelectItem>
                      <SelectItem value="8">8x - 1 journée en 3h</SelectItem>
                      <SelectItem value="16">16x - 2 jours en 3h</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-2">
                    Langues des contenus générés
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {languages.map(lang => (
                      <label key={lang.code} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={enabledLanguages.includes(lang.code)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setEnabledLanguages([...enabledLanguages, lang.code]);
                            } else if (enabledLanguages.length > 1) {
                              setEnabledLanguages(enabledLanguages.filter(l => l !== lang.code));
                            }
                          }}
                          disabled={!isNew}
                        />
                        <span className="font-mono text-sm">{lang.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {scenario?.configuration_questions?.map((q, i) => (
                  <div key={i}>
                    <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-2">
                      {q.question}
                    </label>
                    <Input
                      className="input-matrix"
                      value={configAnswers[q.key] || ""}
                      onChange={(e) => setConfigAnswers({ ...configAnswers, [q.key]: e.target.value })}
                      disabled={!isNew}
                    />
                  </div>
                ))}

                {isNew && (
                  <Button
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider mt-4"
                    onClick={createSession}
                    data-testid="create-session-btn"
                  >
                    Créer la Session
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Participants Panel */}
          {session && activeTab === "participants" && (
            <div className="crisis-panel p-6 lg:col-span-2">
              <h2 className="heading text-xl text-primary mb-6">Participants COMEX ({participants.length})</h2>
              
              <div className="space-y-4 mb-6">
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    className="input-matrix"
                    placeholder="Nom du participant"
                    value={newParticipant.name}
                    onChange={(e) => setNewParticipant({ ...newParticipant, name: e.target.value })}
                    data-testid="participant-name-input"
                  />
                  <Select
                    value={newParticipant.role_id}
                    onValueChange={(v) => setNewParticipant({ ...newParticipant, role_id: v })}
                  >
                    <SelectTrigger className="input-matrix">
                      <SelectValue placeholder="Rôle" />
                    </SelectTrigger>
                    <SelectContent>
                      {scenario?.roles?.map(role => (
                        <SelectItem key={role.id} value={role.id}>{role.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button className="btn-matrix" onClick={addParticipant} data-testid="add-participant-btn">
                  <Plus className="w-4 h-4 mr-2" /> Ajouter Participant
                </Button>
              </div>

              <ScrollArea className="h-[300px]">
                <div className="space-y-2">
                  {participants.map((p, i) => (
                    <motion.div
                      key={p.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className="flex items-center justify-between p-3 bg-secondary/50 border border-border"
                    >
                      <div>
                        <span className="font-semibold">{p.name}</span>
                        <span className="font-mono text-xs text-muted-foreground ml-2">{p.role_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(getAccessUrl(p.access_token), p.id)}
                          title="Copier le lien d'accès"
                        >
                          {copied === p.id ? <Check className="w-4 h-4 text-primary" /> : <Copy className="w-4 h-4" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeParticipant(p.id)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Animators Panel */}
          {session && activeTab === "animators" && (
            <div className="crisis-panel p-6 lg:col-span-2">
              <h2 className="heading text-xl text-primary mb-6">
                Animateurs Secondaires ({animators.length})
              </h2>
              <p className="text-sm text-muted-foreground mb-4">
                Les animateurs jouent les rôles externes (appels téléphoniques, interventions physiques).
                Assignez-leur les rôles qu'ils doivent jouer.
              </p>
              
              <div className="space-y-4 mb-6">
                <Input
                  className="input-matrix"
                  placeholder="Prénom de l'animateur"
                  value={newAnimator.name}
                  onChange={(e) => setNewAnimator({ ...newAnimator, name: e.target.value })}
                  data-testid="animator-name-input"
                />
                
                <div>
                  <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-2">
                    Rôles à jouer
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {getAnimatorRoles().map(role => (
                      <label key={role} className="flex items-center gap-2 cursor-pointer bg-secondary/50 px-3 py-1 border border-border">
                        <Checkbox
                          checked={newAnimator.assigned_roles.includes(role)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setNewAnimator({ ...newAnimator, assigned_roles: [...newAnimator.assigned_roles, role] });
                            } else {
                              setNewAnimator({ ...newAnimator, assigned_roles: newAnimator.assigned_roles.filter(r => r !== role) });
                            }
                          }}
                        />
                        <span className="font-mono text-sm">{role}</span>
                      </label>
                    ))}
                    {getAnimatorRoles().length === 0 && (
                      <span className="text-muted-foreground text-sm">
                        Aucun rôle externe dans ce scénario (pas d'appels téléphoniques ou d'interventions médias)
                      </span>
                    )}
                  </div>
                </div>
                
                <Button className="btn-matrix" onClick={addAnimator} data-testid="add-animator-btn">
                  <Plus className="w-4 h-4 mr-2" /> Ajouter Animateur
                </Button>
              </div>

              <ScrollArea className="h-[300px]">
                <div className="space-y-2">
                  {animators.map((a, i) => (
                    <motion.div
                      key={a.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className="flex items-center justify-between p-4 bg-secondary/50 border border-border"
                    >
                      <div>
                        <span className="font-semibold text-lg">{a.name}</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {a.assigned_roles?.map(role => (
                            <Badge key={role} className="badge-matrix">{role}</Badge>
                          ))}
                          {(!a.assigned_roles || a.assigned_roles.length === 0) && (
                            <span className="text-xs text-muted-foreground">Aucun rôle assigné</span>
                          )}
                        </div>
                        <div className="mt-2">
                          <span className="font-mono text-xs text-muted-foreground">Token: </span>
                          <span className="font-mono text-xs text-primary">{a.access_token}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(getAccessUrl(a.access_token), `anim-${a.id}`)}
                          title="Copier le lien d'accès"
                        >
                          {copied === `anim-${a.id}` ? <Check className="w-4 h-4 text-primary" /> : <Copy className="w-4 h-4" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeAnimator(a.id)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* QR Code & Launch Panel */}
          {session && (activeTab === "config" || (participants.length > 0 || animators.length > 0)) && (
            <div className="crisis-panel p-6">
              <h2 className="heading text-xl text-primary mb-6">Lancement</h2>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-secondary/30 border border-border">
                  <div>
                    <h3 className="heading text-sm text-muted-foreground">QR Code Session</h3>
                    <p className="font-mono text-[10px] text-muted-foreground">Scannez pour rejoindre</p>
                  </div>
                  <div className="qr-container">
                    <QRCodeSVG
                      value={`${window.location.origin}/?code=${session.access_code}`}
                      size={100}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Participants COMEX:</span>
                    <span className="font-mono text-primary">{participants.length}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Animateurs secondaires:</span>
                    <span className="font-mono text-primary">{animators.length}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Compression:</span>
                    <span className="font-mono text-primary">x{session.time_compression}</span>
                  </div>
                </div>
                
                <Button
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider mt-4"
                  onClick={() => navigate(`/animator/${session.id}`)}
                  data-testid="start-simulation-btn"
                >
                  <Play className="w-4 h-4 mr-2" /> Lancer la Simulation
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Animator Dashboard
const AnimatorDashboard = () => {
  const { sessionId } = useParams();
  const [session, setSession] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [messages, setMessages] = useState([]);
  const [scenarioTime, setScenarioTime] = useState("00:00");
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedLanguages, setSelectedLanguages] = useState(["fr"]);
  const [generatingContent, setGeneratingContent] = useState(false);
  const [customContent, setCustomContent] = useState("");
  const [customContentType, setCustomContentType] = useState("flash_info");

  const fetchData = useCallback(async () => {
    try {
      const [sessionRes, messagesRes] = await Promise.all([
        axios.get(`${API}/sessions/${sessionId}`),
        axios.get(`${API}/sessions/${sessionId}/messages`)
      ]);
      setSession(sessionRes.data);
      setMessages(messagesRes.data.messages || []);
      
      const scenRes = await axios.get(`${API}/scenarios/${sessionRes.data.scenario_id}`);
      setScenario(scenRes.data);
      
      const timeRes = await axios.get(`${API}/sessions/${sessionId}/time`);
      setScenarioTime(timeRes.data.scenario_time);
    } catch (err) {
      toast.error("Erreur lors du chargement");
    }
    setLoading(false);
  }, [sessionId]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const controlSession = async (action) => {
    try {
      await axios.post(`${API}/sessions/${sessionId}/control?action=${action}`);
      fetchData();
      toast.success(`Action: ${action}`);
    } catch (err) {
      toast.error("Erreur de contrôle");
    }
  };

  const triggerEvent = async (eventIndex) => {
    try {
      setGeneratingContent(true);
      await axios.post(`${API}/sessions/${sessionId}/trigger-event/${eventIndex}?languages=${selectedLanguages.join(',')}`);
      fetchData();
      toast.success("Événement déclenché");
    } catch (err) {
      toast.error("Erreur lors du déclenchement");
    }
    setGeneratingContent(false);
  };

  const sendCustomContent = async () => {
    if (!customContent.trim()) return;
    try {
      await axios.post(`${API}/sessions/${sessionId}/messages`, {
        message_type: customContentType,
        sender: "Animateur",
        content: customContent,
        target_role_ids: [],
        platform: customContentType === "social_post" ? "Twitter" : null
      });
      setCustomContent("");
      fetchData();
      toast.success("Contenu envoyé");
    } catch (err) {
      toast.error("Erreur d'envoi");
    }
  };

  const generateAIContent = async (type, context) => {
    try {
      setGeneratingContent(true);
      const res = await axios.post(`${API}/sessions/${sessionId}/generate`, {
        content_type: type,
        context: context,
        platform: type === "social_post" ? "Twitter" : "France Info",
        language: selectedLanguages[0] || "fr",
        tone: "urgent"
      });
      setCustomContent(res.data.generated_content);
      toast.success("Contenu généré par IA");
    } catch (err) {
      toast.error("Erreur de génération IA");
    }
    setGeneratingContent(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen matrix-bg flex items-center justify-center">
        <div className="loading-dot" />
        <div className="loading-dot" />
        <div className="loading-dot" />
      </div>
    );
  }

  return (
    <div className="min-h-screen matrix-bg">
      <ScanlineOverlay />
      <PauseOverlay show={session?.status === 'paused'} />
      
      {/* Header */}
      <div className="crisis-header">
        <div className="flex items-center gap-6">
          <Shield className="w-8 h-8 text-primary" />
          <div>
            <h1 className="heading text-lg text-primary">{session?.company_name}</h1>
            <p className="font-mono text-xs text-muted-foreground">{scenario?.name}</p>
          </div>
        </div>
        <ScenarioClock 
          time={scenarioTime} 
          status={session?.status} 
          compression={session?.time_compression} 
        />
        <div className="flex items-center gap-2">
          {session?.status !== 'running' ? (
            <Button
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={() => controlSession(session?.status === 'setup' ? 'start' : 'resume')}
              data-testid="play-btn"
            >
              <Play className="w-4 h-4 mr-2" /> {session?.status === 'setup' ? 'Démarrer' : 'Reprendre'}
            </Button>
          ) : (
            <Button
              className="bg-yellow-600 text-white hover:bg-yellow-700"
              onClick={() => controlSession('pause')}
              data-testid="pause-btn"
            >
              <Pause className="w-4 h-4 mr-2" /> Pause
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => controlSession('stop')}
            data-testid="stop-btn"
          >
            <Square className="w-4 h-4" />
          </Button>
          <Select
            value={String(session?.time_compression)}
            onValueChange={(v) => controlSession(`speed_${v}`)}
          >
            <SelectTrigger className="w-24 input-matrix">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1x</SelectItem>
              <SelectItem value="2">2x</SelectItem>
              <SelectItem value="4">4x</SelectItem>
              <SelectItem value="8">8x</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Intensity Meter */}
      <div className="px-4 py-2">
        <IntensityMeter sessionId={sessionId} messages={messages} />
      </div>

      {/* Main Content */}
      <div className="dashboard-grid">
        {/* Timeline & Events */}
        <div className="col-span-12 lg:col-span-4 crisis-panel p-4">
          <h2 className="heading text-lg text-primary mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5" /> Timeline
          </h2>
          
          {/* Language Selection */}
          <div className="mb-4">
            <span className="font-mono text-xs text-muted-foreground">Langues:</span>
            <div className="flex flex-wrap gap-2 mt-1">
              {session?.enabled_languages?.map(lang => (
                <label key={lang} className="flex items-center gap-1 cursor-pointer">
                  <Checkbox
                    checked={selectedLanguages.includes(lang)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedLanguages([...selectedLanguages, lang]);
                      } else if (selectedLanguages.length > 1) {
                        setSelectedLanguages(selectedLanguages.filter(l => l !== lang));
                      }
                    }}
                  />
                  <span className="font-mono text-xs uppercase">{lang}</span>
                </label>
              ))}
            </div>
          </div>

          <ScrollArea className="h-[calc(100vh-280px)]">
            <div className="space-y-2">
              {scenario?.events?.map((event, index) => {
                const isTriggered = messages.some(m => m.event_id === event.id);
                return (
                  <motion.div
                    key={event.id || index}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.05 }}
                    className={`p-3 border cursor-pointer transition-colors ${
                      isTriggered 
                        ? 'bg-secondary/30 border-muted-foreground/30' 
                        : 'bg-secondary/50 border-border hover:border-primary/50'
                    }`}
                    onClick={() => !isTriggered && setSelectedEvent({ event, index })}
                    data-testid={`event-${index}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-mono text-xs text-primary">T+{event.time_offset_minutes}min</span>
                      <Badge className={`badge-matrix ${isTriggered ? 'opacity-50' : ''}`}>
                        {event.event_type}
                      </Badge>
                    </div>
                    <h4 className="text-sm font-semibold">{event.title}</h4>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{event.content}</p>
                    {!isTriggered && (
                      <Button
                        size="sm"
                        className="mt-2 bg-primary/20 text-primary hover:bg-primary/30 w-full"
                        onClick={(e) => {
                          e.stopPropagation();
                          triggerEvent(index);
                        }}
                        disabled={generatingContent}
                      >
                        <Send className="w-3 h-3 mr-1" /> Déclencher
                      </Button>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </ScrollArea>
        </div>

        {/* Message Feed */}
        <div className="col-span-12 lg:col-span-4 crisis-panel p-4">
          <h2 className="heading text-lg text-primary mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5" /> Flux de Messages ({messages.length})
          </h2>
          <ScrollArea className="h-[calc(100vh-220px)]">
            <div className="space-y-2">
              {messages.map((msg, i) => (
                <MessageCard key={msg.id || i} message={msg} />
              ))}
              {messages.length === 0 && (
                <p className="text-center text-muted-foreground font-mono text-sm py-8">
                  Aucun message envoyé
                </p>
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Custom Content & Participants */}
        <div className="col-span-12 lg:col-span-4 space-y-4">
          {/* Custom Content Panel */}
          <div className="crisis-panel p-4">
            <h2 className="heading text-lg text-primary mb-4 flex items-center gap-2">
              <Terminal className="w-5 h-5" /> Contenu Personnalisé
            </h2>
            <div className="space-y-3">
              <Select value={customContentType} onValueChange={setCustomContentType}>
                <SelectTrigger className="input-matrix">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="flash_info">Flash Info</SelectItem>
                  <SelectItem value="social_post">Post Réseau Social</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                </SelectContent>
              </Select>
              <Textarea
                className="input-matrix min-h-[100px]"
                placeholder="Écrivez votre contenu ou générez avec l'IA..."
                value={customContent}
                onChange={(e) => setCustomContent(e.target.value)}
              />
              <div className="flex gap-2">
                <Button
                  className="flex-1 btn-matrix"
                  onClick={() => generateAIContent(customContentType, `Générer un ${customContentType} pour une crise ${scenario?.crisis_type}`)}
                  disabled={generatingContent}
                >
                  <Zap className="w-4 h-4 mr-2" /> Générer IA
                </Button>
                <Button
                  className="flex-1 bg-primary text-primary-foreground"
                  onClick={sendCustomContent}
                  disabled={!customContent.trim()}
                >
                  <Send className="w-4 h-4 mr-2" /> Envoyer
                </Button>
              </div>
            </div>
          </div>

          {/* Manual Input Panel - Enhanced */}
          <ManualInputPanel 
            sessionId={sessionId} 
            participants={session?.participants || []}
            onSent={fetchData}
          />

          {/* Participants Panel */}
          <div className="crisis-panel p-4">
            <h2 className="heading text-lg text-primary mb-4 flex items-center gap-2">
              <Users className="w-5 h-5" /> Participants ({session?.participants?.length || 0})
            </h2>
            <ScrollArea className="h-[200px]">
              <div className="space-y-2">
                {session?.participants?.map((p, i) => (
                  <div
                    key={p.id}
                    className="flex items-center justify-between p-2 bg-secondary/30 border border-border"
                  >
                    <div className="flex items-center gap-2">
                      <span className="status-dot active" />
                      <span className="text-sm font-medium">{p.name}</span>
                    </div>
                    <Badge className="badge-matrix">{p.role_name}</Badge>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        </div>
      </div>
    </div>
  );
};

// Player Dashboard
const PlayerDashboard = () => {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const accessToken = searchParams.get('token');
  
  const [session, setSession] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [participant, setParticipant] = useState(null);
  const [messages, setMessages] = useState([]);
  const [scenarioTime, setScenarioTime] = useState("00:00");
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [responseContent, setResponseContent] = useState("");

  const fetchData = useCallback(async () => {
    try {
      if (accessToken) {
        const joinRes = await axios.get(`${API}/join/${accessToken}`);
        setSession(joinRes.data.session);
        setScenario(joinRes.data.scenario);
        setParticipant(joinRes.data.participant);
        
        const messagesRes = await axios.get(
          `${API}/sessions/${joinRes.data.session.id}/messages?role_id=${joinRes.data.participant.role_id}`
        );
        setMessages(messagesRes.data.messages || []);
        
        const timeRes = await axios.get(`${API}/sessions/${joinRes.data.session.id}/time`);
        setScenarioTime(timeRes.data.scenario_time);
      } else {
        const sessionRes = await axios.get(`${API}/sessions/${sessionId}`);
        setSession(sessionRes.data);
        
        const scenRes = await axios.get(`${API}/scenarios/${sessionRes.data.scenario_id}`);
        setScenario(scenRes.data);
        
        const messagesRes = await axios.get(`${API}/sessions/${sessionId}/messages`);
        setMessages(messagesRes.data.messages || []);
        
        const timeRes = await axios.get(`${API}/sessions/${sessionId}/time`);
        setScenarioTime(timeRes.data.scenario_time);
      }
    } catch (err) {
      toast.error("Erreur lors du chargement");
    }
    setLoading(false);
  }, [sessionId, accessToken]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 3000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const sendResponse = async () => {
    if (!responseContent.trim() || !selectedMessage) return;
    try {
      await axios.post(`${API}/sessions/${session.id}/messages`, {
        message_type: selectedMessage.message_type,
        sender: participant?.name || "Joueur",
        content: responseContent,
        recipient: selectedMessage.sender,
        is_response: true,
        response_to_id: selectedMessage.id
      });
      setResponseContent("");
      setSelectedMessage(null);
      fetchData();
      toast.success("Réponse envoyée");
    } catch (err) {
      toast.error("Erreur d'envoi");
    }
  };

  const filterMessages = (type) => {
    if (type === "all") return messages;
    return messages.filter(m => {
      if (type === "email") return m.message_type === "email";
      if (type === "sms") return m.message_type === "sms";
      if (type === "social") return ["social_post", "twitter", "facebook", "instagram"].includes(m.message_type) || m.platform;
      if (type === "press") return ["flash_info", "press_article", "media_arrival"].includes(m.message_type);
      return true;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen matrix-bg flex items-center justify-center">
        <div className="loading-dot" />
        <div className="loading-dot" />
        <div className="loading-dot" />
      </div>
    );
  }

  return (
    <div className="min-h-screen matrix-bg">
      <ScanlineOverlay />
      <PauseOverlay show={session?.status === 'paused'} />
      
      {/* Header */}
      <div className="crisis-header">
        <div className="flex items-center gap-4">
          <Shield className="w-8 h-8 text-primary" />
          <div>
            <h1 className="heading text-lg text-primary">{session?.company_name}</h1>
            <p className="font-mono text-xs text-muted-foreground">
              {participant ? `${participant.name} - ${participant.role_name}` : 'Observateur'}
            </p>
          </div>
        </div>
        <ScenarioClock 
          time={scenarioTime} 
          status={session?.status} 
          compression={session?.time_compression} 
        />
      </div>

      {/* Main Content */}
      <div className="p-4 max-w-6xl mx-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-card border border-border p-1 mb-4">
            <TabsTrigger value="all" className="tab-matrix data-[state=active]:text-primary data-[state=active]:border-primary">
              <Activity className="w-4 h-4 mr-2" /> Tous
              <Badge className="ml-2 badge-matrix">{messages.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="email" className="tab-matrix data-[state=active]:text-primary data-[state=active]:border-primary">
              <Mail className="w-4 h-4 mr-2" /> Emails
            </TabsTrigger>
            <TabsTrigger value="sms" className="tab-matrix data-[state=active]:text-primary data-[state=active]:border-primary">
              <MessageSquare className="w-4 h-4 mr-2" /> SMS
            </TabsTrigger>
            <TabsTrigger value="social" className="tab-matrix data-[state=active]:text-primary data-[state=active]:border-primary">
              <Globe className="w-4 h-4 mr-2" /> Réseaux
            </TabsTrigger>
            <TabsTrigger value="press" className="tab-matrix data-[state=active]:text-primary data-[state=active]:border-primary">
              <Newspaper className="w-4 h-4 mr-2" /> Presse
            </TabsTrigger>
          </TabsList>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Message List */}
            <div className="lg:col-span-2 crisis-panel p-4">
              <ScrollArea className="h-[calc(100vh-220px)]">
                <div className="space-y-3">
                  <AnimatePresence>
                    {filterMessages(activeTab).map((msg, i) => (
                      <motion.div
                        key={msg.id || i}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ delay: i * 0.05 }}
                      >
                        <MessageCard
                          message={msg}
                          onClick={() => setSelectedMessage(msg)}
                        />
                      </motion.div>
                    ))}
                  </AnimatePresence>
                  {filterMessages(activeTab).length === 0 && (
                    <p className="text-center text-muted-foreground font-mono text-sm py-8">
                      Aucun message dans cette catégorie
                    </p>
                  )}
                </div>
              </ScrollArea>
            </div>

            {/* Message Detail / Response */}
            <div className="crisis-panel p-4">
              {selectedMessage ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Badge className="badge-matrix">{selectedMessage.message_type}</Badge>
                    <span className="font-mono text-xs text-muted-foreground">{selectedMessage.scenario_time}</span>
                  </div>
                  <div>
                    <span className="font-mono text-xs text-muted-foreground">De:</span>
                    <p className="font-semibold text-primary">{selectedMessage.sender}</p>
                  </div>
                  {selectedMessage.subject && (
                    <div>
                      <span className="font-mono text-xs text-muted-foreground">Objet:</span>
                      <p className="font-semibold">{selectedMessage.subject}</p>
                    </div>
                  )}
                  <div>
                    <span className="font-mono text-xs text-muted-foreground">Message:</span>
                    <p className="text-sm mt-1 whitespace-pre-wrap">{selectedMessage.content}</p>
                  </div>
                  
                  <div className="pt-4 border-t border-border">
                    <span className="font-mono text-xs text-muted-foreground">Votre réponse:</span>
                    <Textarea
                      className="input-matrix mt-2 min-h-[100px]"
                      placeholder="Rédigez votre réponse..."
                      value={responseContent}
                      onChange={(e) => setResponseContent(e.target.value)}
                    />
                    <Button
                      className="w-full mt-2 bg-primary text-primary-foreground"
                      onClick={sendResponse}
                      disabled={!responseContent.trim()}
                    >
                      <Send className="w-4 h-4 mr-2" /> Envoyer la réponse
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center py-12">
                  <Eye className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground font-mono text-sm">
                    Sélectionnez un message pour voir les détails et répondre
                  </p>
                </div>
              )}
            </div>
          </div>
        </Tabs>
      </div>
    </div>
  );
};

// Print Fiches Component - Generate participant connection sheets with QR codes
const PrintFiches = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [loading, setLoading] = useState(true);
  const baseUrl = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || window.location.origin;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const sessionRes = await axios.get(`${API}/sessions/${sessionId}`);
        setSession(sessionRes.data);
        
        const scenarioRes = await axios.get(`${API}/scenarios/${sessionRes.data.scenario_id}`);
        setScenario(scenarioRes.data);
      } catch (err) {
        toast.error("Erreur de chargement");
      }
      setLoading(false);
    };
    fetchData();
  }, [sessionId]);

  const generateQRCodeUrl = (text) => {
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(text)}`;
  };

  const getParticipantUrl = (participant) => {
    return `${baseUrl}/player/${sessionId}?token=${participant.access_token}`;
  };

  const getRoleMission = (roleName) => {
    const missions = {
      'DG': { title: 'Directeur Général', desc: 'Vous êtes le décideur ultime. Coordonnez les équipes, prenez les décisions stratégiques et gérez la communication de crise au plus haut niveau.' },
      'DirCom': { title: 'Directeur Communication', desc: 'Vous gérez les relations avec les médias et les réseaux sociaux. Préparez les éléments de langage et coordonnez la communication externe.' },
      'DRH': { title: 'Directeur RH', desc: 'Vous gérez la communication interne, les relations avec les syndicats et le bien-être des salariés pendant la crise.' },
      'DirOps': { title: 'Directeur Opérations', desc: 'Vous coordonnez les opérations terrain, l\'évacuation et la liaison avec les services de secours.' },
      'DirQualite': { title: 'Directeur Qualité/QHSE', desc: 'Vous gérez les aspects sécurité, environnement et conformité réglementaire.' },
      'DirJuridique': { title: 'Directeur Juridique', desc: 'Vous anticipez les risques juridiques et conseillez sur les responsabilités.' }
    };
    return missions[roleName] || { title: roleName, desc: 'Participez activement à la gestion de cette crise selon votre rôle.' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4">
      {/* Header - Not printed */}
      <div className="max-w-4xl mx-auto mb-8 print:hidden">
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => navigate('/admin')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Retour
          </Button>
          <h1 className="text-xl font-bold text-primary">📄 Fiches de Connexion</h1>
          <Button onClick={() => window.print()} className="bg-green-600 hover:bg-green-700">
            <FileText className="w-4 h-4 mr-2" /> Imprimer tout
          </Button>
        </div>
        <p className="text-center text-muted-foreground mt-4">
          Session : <strong>{session?.company_name}</strong> | Scénario : <strong>{scenario?.name}</strong> | 
          Participants : <strong>{session?.participants?.length || 0}</strong>
        </p>
      </div>

      {/* Animator Sheet */}
      <div className="max-w-2xl mx-auto mb-8 print:break-after-page">
        <div className="bg-gradient-to-b from-red-900/20 to-background border-2 border-red-500/50 rounded-lg p-8 print:border-black print:bg-white">
          <div className="text-center mb-6">
            <div className="text-3xl font-bold text-red-500 mb-2">🎯 CRISIS SIMULATOR</div>
            <div className="text-xl font-semibold bg-red-500 text-white px-4 py-2 rounded inline-block">
              GUIDE ANIMATEUR
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-card/50 p-4 rounded border border-border">
              <h3 className="font-semibold text-red-400 mb-2">🔐 Connexion Animateur</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">URL :</span>
                  <div className="font-mono text-primary break-all">{baseUrl}/login</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Email :</span>
                  <div className="font-mono">jb@simulationdecrise.fr</div>
                </div>
              </div>
            </div>

            <div className="bg-card/50 p-4 rounded border border-border">
              <h3 className="font-semibold text-red-400 mb-2">🚀 Démarrage rapide</h3>
              <ol className="text-sm space-y-1 list-decimal list-inside">
                <li>Connectez-vous avec vos identifiants</li>
                <li>Sélectionnez la session <strong>"{session?.company_name}"</strong></li>
                <li>Cliquez sur <strong>"Animer"</strong></li>
                <li>Vérifiez que tous les participants sont connectés</li>
                <li>Cliquez sur <strong>"▶ Démarrer"</strong></li>
              </ol>
            </div>

            <div className="bg-card/50 p-4 rounded border border-border">
              <h3 className="font-semibold text-red-400 mb-2">🎛️ Contrôles</h3>
              <div className="grid grid-cols-3 gap-2 text-center text-sm">
                <div className="bg-green-900/30 p-2 rounded">▶️ Démarrer</div>
                <div className="bg-yellow-900/30 p-2 rounded">⏸️ Pause</div>
                <div className="bg-red-900/30 p-2 rounded">⏹️ Arrêter</div>
                <div className="bg-blue-900/30 p-2 rounded">📻 Flash Radio</div>
                <div className="bg-purple-900/30 p-2 rounded">📝 Message</div>
                <div className="bg-orange-900/30 p-2 rounded">📞 Appel</div>
              </div>
            </div>

            <div className="bg-yellow-900/30 p-4 rounded border border-yellow-500/50 text-sm">
              <strong>⚠️ Important :</strong> Les messages du scénario arrivent automatiquement. Utilisez le panneau d'envoi manuel pour improviser ou adapter.
            </div>
          </div>
        </div>
      </div>

      {/* Participant Sheets */}
      {session?.participants?.map((participant, index) => {
        const mission = getRoleMission(participant.role_name);
        const participantUrl = getParticipantUrl(participant);
        
        return (
          <div key={participant.id} className="max-w-2xl mx-auto mb-8 print:break-after-page">
            <div className="bg-gradient-to-b from-green-900/20 to-background border-2 border-green-500/50 rounded-lg p-8 print:border-black print:bg-white">
              <div className="text-center mb-6">
                <div className="text-2xl font-bold text-green-500 mb-2">🎯 CRISIS SIMULATOR</div>
                <div className="text-sm text-muted-foreground mb-2">Fiche Participant</div>
                <div className="text-2xl font-bold bg-green-500 text-white px-6 py-3 rounded inline-block">
                  {participant.role_name} - {participant.name || 'Joueur'}
                </div>
              </div>

              {/* QR Code */}
              <div className="text-center mb-6">
                <div className="inline-block bg-white p-2 rounded">
                  <img 
                    src={generateQRCodeUrl(participantUrl)} 
                    alt="QR Code de connexion"
                    className="w-32 h-32"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2">Scannez ce QR code avec votre téléphone</p>
              </div>

              {/* Connection URL */}
              <div className="bg-card/50 p-3 rounded border border-border mb-4">
                <span className="text-xs text-muted-foreground">Ou tapez cette URL :</span>
                <div className="font-mono text-xs text-green-400 break-all mt-1">{participantUrl}</div>
              </div>

              {/* Mission */}
              <div className="bg-card/50 p-4 rounded border border-border mb-4">
                <h3 className="font-semibold text-green-400 mb-2">🎯 Votre Mission : {mission.title}</h3>
                <p className="text-sm">{mission.desc}</p>
              </div>

              {/* Instructions */}
              <div className="bg-card/50 p-4 rounded border border-border mb-4">
                <h3 className="font-semibold text-green-400 mb-2">📱 Comment participer</h3>
                <ol className="text-sm space-y-1 list-decimal list-inside">
                  <li>Scannez le QR code ou tapez l'URL</li>
                  <li>Attendez le signal de l'animateur</li>
                  <li>Consultez vos messages et répondez</li>
                  <li>Communiquez avec votre équipe</li>
                </ol>
              </div>

              {/* Session Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-card/30 p-2 rounded text-center">
                  <div className="text-muted-foreground text-xs">Session</div>
                  <div className="font-semibold">{session?.company_name}</div>
                </div>
                <div className="bg-card/30 p-2 rounded text-center">
                  <div className="text-muted-foreground text-xs">Scénario</div>
                  <div className="font-semibold">{scenario?.name}</div>
                </div>
              </div>

              <div className="text-center text-xs text-muted-foreground mt-4">
                En cas de problème, contactez l'animateur
              </div>
            </div>
          </div>
        );
      })}

      {/* Observer Sheet */}
      <div className="max-w-2xl mx-auto mb-8 print:break-after-page">
        <div className="bg-gradient-to-b from-purple-900/20 to-background border-2 border-purple-500/50 rounded-lg p-8 print:border-black print:bg-white">
          <div className="text-center mb-6">
            <div className="text-2xl font-bold text-purple-500 mb-2">👁️ CRISIS SIMULATOR</div>
            <div className="text-sm text-muted-foreground mb-2">Fiche Observateur</div>
            <div className="text-2xl font-bold bg-purple-500 text-white px-6 py-3 rounded inline-block">
              MODE OBSERVATION - DRH
            </div>
          </div>

          {/* QR Code for Observer */}
          <div className="text-center mb-6">
            <div className="inline-block bg-white p-2 rounded">
              <img 
                src={generateQRCodeUrl(`${window.location.origin}/observer/${sessionId}`)} 
                alt="QR Code Observateur"
                className="w-32 h-32"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">Scannez pour accéder au Dashboard Observateur</p>
          </div>

          {/* Connection URL */}
          <div className="bg-card/50 p-3 rounded border border-border mb-4">
            <span className="text-xs text-muted-foreground">URL du Dashboard :</span>
            <div className="font-mono text-xs text-purple-400 break-all mt-1">
              {window.location.origin}/observer/{sessionId}
            </div>
          </div>

          {/* Recommended Setup */}
          <div className="bg-card/50 p-4 rounded border border-border mb-4">
            <h3 className="font-semibold text-purple-400 mb-2">🖥️ Configuration Recommandée</h3>
            <ul className="text-sm space-y-1 list-disc list-inside">
              <li><strong>Écran :</strong> Grand écran (TV ou moniteur 24"+)</li>
              <li><strong>Résolution :</strong> 1920x1080 minimum</li>
              <li><strong>Navigateur :</strong> Chrome ou Firefox en plein écran (F11)</li>
              <li><strong>Position :</strong> Visible par tous les observateurs</li>
            </ul>
          </div>

          {/* What you'll see */}
          <div className="bg-card/50 p-4 rounded border border-border mb-4">
            <h3 className="font-semibold text-purple-400 mb-2">📊 Données Affichées en Temps Réel</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <strong className="text-purple-300">📥 Inputs reçus</strong>
                <ul className="text-xs text-muted-foreground ml-3">
                  <li>Articles presse</li>
                  <li>Posts réseaux sociaux</li>
                  <li>Emails</li>
                  <li>Appels téléphoniques</li>
                  <li>Flashs radio</li>
                </ul>
              </div>
              <div>
                <strong className="text-purple-300">📤 Réponses équipe</strong>
                <ul className="text-xs text-muted-foreground ml-3">
                  <li>Communiqués de presse</li>
                  <li>Réponses réseaux</li>
                  <li>Réponses emails</li>
                  <li>Appels traités</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Indicators */}
          <div className="bg-purple-900/30 p-4 rounded border border-purple-500/50 mb-4">
            <h3 className="font-semibold text-purple-400 mb-2">📈 Indicateurs de Performance</h3>
            <div className="grid grid-cols-3 gap-2 text-center text-sm">
              <div className="bg-card/30 p-2 rounded">
                <div className="text-lg">📧</div>
                <div className="text-xs">Taux réponse emails</div>
              </div>
              <div className="bg-card/30 p-2 rounded">
                <div className="text-lg">📱</div>
                <div className="text-xs">Taux réponse réseaux</div>
              </div>
              <div className="bg-card/30 p-2 rounded">
                <div className="text-lg">📞</div>
                <div className="text-xs">Appels traités</div>
              </div>
              <div className="bg-card/30 p-2 rounded">
                <div className="text-lg">⚡</div>
                <div className="text-xs">Indice réactivité</div>
              </div>
              <div className="bg-card/30 p-2 rounded">
                <div className="text-lg">👥</div>
                <div className="text-xs">Statut équipe</div>
              </div>
              <div className="bg-card/30 p-2 rounded">
                <div className="text-lg">📊</div>
                <div className="text-xs">Actions en direct</div>
              </div>
            </div>
          </div>

          {/* Note */}
          <div className="text-center text-sm text-muted-foreground">
            <strong>Mode lecture seule</strong> - Aucune interaction possible avec la simulation
          </div>
        </div>
      </div>

      {/* No participants warning */}
      {(!session?.participants || session.participants.length === 0) && (
        <div className="max-w-2xl mx-auto text-center py-12">
          <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Aucun participant n'a été ajouté à cette session.</p>
          <Button 
            className="mt-4" 
            onClick={() => navigate(`/setup/${session?.scenario_id}/${sessionId}`)}
          >
            Ajouter des participants
          </Button>
        </div>
      )}

      {/* Print styles */}
      <style>{`
        @media print {
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
          .print\\:break-after-page { page-break-after: always; }
          .print\\:border-black { border-color: #333 !important; }
          .print\\:bg-white { background: white !important; }
        }
      `}</style>
    </div>
  );
};

// Scenario Editor Wrappers
const ScenarioTemplatesListWrapper = () => {
  const { token } = useAuth();
  return <ScenarioTemplatesList token={token} />;
};

const ScenarioEditorWrapper = () => {
  const { token } = useAuth();
  return <ScenarioEditor token={token} />;
};

// App Component
function App() {
  return (
    <AuthProvider>
      <div className="App">
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: '#0a0a0a',
              border: '1px solid #00FF41',
              color: '#E5E5E5',
              fontFamily: 'JetBrains Mono, monospace',
            },
          }}
        />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<AdminLogin />} />
            <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/scenarios" element={<ProtectedRoute><ScenarioTemplatesListWrapper /></ProtectedRoute>} />
            <Route path="/admin/scenarios/:templateId" element={<ProtectedRoute><ScenarioEditorWrapper /></ProtectedRoute>} />
            <Route path="/scenarios" element={<ProtectedRoute><ScenarioSelection /></ProtectedRoute>} />
            <Route path="/setup/:scenarioId/:sessionId" element={<ProtectedRoute><SessionSetup /></ProtectedRoute>} />
            <Route path="/setup/:scenarioId" element={<ProtectedRoute><SessionSetup /></ProtectedRoute>} />
            <Route path="/animator/:sessionId" element={<ProtectedRoute><AnimatorDashboard /></ProtectedRoute>} />
            <Route path="/animator-view/:sessionId" element={<AnimatorSecondaryDashboard />} />
            <Route path="/animator-secondary/:sessionId" element={<AnimatorSecondaryDashboard />} />
            <Route path="/print-fiches/:sessionId" element={<ProtectedRoute><PrintFiches /></ProtectedRoute>} />
            <Route path="/player/:sessionId" element={<PlayerDashboard />} />
            <Route path="/participant-select" element={<ParticipantSessionSelect />} />
            <Route path="/demo" element={<DemoSelection />} />
            <Route path="/demo/multi" element={<DemoMultiView />} />
            <Route path="/demo/client" element={<DemoClientDashboard />} />
            <Route path="/demo/admin-request" element={<DemoAdminRequest />} />
            <Route path="/demo/admin-login" element={<DemoAdminLogin />} />
            <Route path="/demo/admin" element={<DemoAdminDashboard />} />
            <Route path="/demo/observer" element={<DemoObserverDashboard />} />
            <Route path="/animator/demo" element={<DemoAnimatorDashboard />} />
            <Route path="/observer/:sessionId" element={<ObserverDashboard />} />
            <Route path="/observer" element={<ObserverDashboard />} />
            <Route path="/quote" element={<QuoteRequestPage />} />
            <Route path="/guides" element={<UserGuidesPage />} />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
